import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Crown, ShieldOff, UserCheck, UserMinus, UserX } from 'lucide-react';
import { FalhaNoBloco, porBlocos } from '@malgoos/shared';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { FormasAcesso, IconesFormas } from '../../componentes/formas-acesso';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { SeletorEstilo } from '../../componentes/seletor-estilo';
import { ehEstilo, ESTILO_PADRAO, type Estilo } from '../../nucleo/estilos';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { anunciarOcultos } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, data, email, nomeDeUsuario, nomePessoa } from '../../nucleo/formato';
import { NomeComFicha } from './link-ficha';
import { useSessao, veTodasAsEmpresas, type DadosSessao } from '../../nucleo/sessao';
import { usePapeis } from './MatrizPermissoes';
import { AcessoDoMembro } from './AcessoDoMembro';
import { nomeDoPapel, type Membro } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { ExcecoesMembro } from '../../nucleo/notificacoes/PadroesPapel';

interface Catalogos {
  empresas: { id: string; razao_social: string; nome_fantasia: string | null }[];
}

/** Membros do workspace: papéis, estado, último acesso; o detalhe abre numa gaveta. */
/** Review r41 visual (B1): "sem nenhuma marcada = todas" só para quem vê todas; o restrito precisa manter ao menos uma. */
export const chaveAjudaEmpresas = (s: Pick<DadosSessao, 'empresas'>) => (veTodasAsEmpresas(s) ? 'usuarios.empresasAjuda' : 'usuarios.empresasAjudaRestrito');

/**
 * Review r42 visual (M2): para quem não vê todas as empresas, o membro SEM lista vê todas: a gaveta mostra isso (sem caixas
 * nem "Salvar empresas", que sempre falharia) em vez de caixas desmarcadas. O membro com empresas que o ator não vê
 * ganha a nota "e outras empresas que você não vê" (a lista dele não é só o que está marcado).
 */
export function resumoEmpresasMembro(s: Pick<DadosSessao, 'empresas'>, empresaIds: readonly string[], visiveis: readonly string[]) {
  if (veTodasAsEmpresas(s)) return { veTodas: false, outras: false };
  return { veTodas: empresaIds.length === 0, outras: empresaIds.some((id) => !visiveis.includes(id)) };
}

/**
 * Review r43 visual (B2): para o restrito, o membro sem lista (vê todas) não tem caixas; a ajuda das caixas ("manter ao
 * menos uma marcada", "exceção abaixo") contradizia a linha "vê todas". Nesse estado fica só a explicação de "vê todas".
 */
export const mostraAjudaEmpresas = (s: Pick<DadosSessao, 'empresas'>, empresaIds: readonly string[]) => !resumoEmpresasMembro(s, empresaIds, []).veTodas;

export function Membros() {
  const { t } = useTranslation('admin');
  const [situacao, setSituacao] = useState('todos');
  // Val1 (28/10): filtro "com exceção" (acesso personalizado), para revisar as exceções.
  const [acesso, setAcesso] = useState('todos');
  const [aberto, setAberto] = useState<Membro | null>(null);
  const s = useSessao();
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  // N5 (28/10): ação em massa "Definir estilo" (fixa o estilo das pessoas marcadas, ou solta a fixação).
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [loteEstilo, setLoteEstilo] = useState(false);
  const [estiloLote, setEstiloLote] = useState<Estilo | null>(null);
  // rcrm4 ROB-M1: a seleção vai em blocos de 50 (TETO_ACAO_EM_MASSA); se um bloco falha depois de outros gravados, os feitos
  // saem da seleção e o aviso diz "X de N" (repetir manda só o resto).
  const aplicarEstilo = useMutation({
    mutationFn: async () => {
      const respostas = await porBlocos([...marcados], (bloco) => api<{ alterados: number }>('PUT', '/admin/membros/estilo', { usuario_ids: bloco, estilo: estiloLote }));
      return { alterados: respostas.reduce((t, r) => t + r.alterados, 0) };
    },
    onSuccess: async (r) => {
      setLoteEstilo(false);
      setMarcados(new Set());
      await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
      toast.sucesso(t('usuarios.estiloAplicado', { count: r.alterados }));
    },
    onError: async (e) => {
      if (!(e instanceof FalhaNoBloco)) return toast.erro(mensagemDeErro(e));
      const feitos = new Set(e.feitos as string[]);
      setMarcados((m) => new Set([...m].filter((x) => !feitos.has(x))));
      setLoteEstilo(false);
      await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
      toast.erro(t('usuarios.estiloParcial', { feitos: e.feitos.length, total: e.total, erro: mensagemDeErro(e.causa) }));
    },
  });
  const colunas: Coluna<Membro>[] = [
    {
      chave: 'nome',
      rotulo: t('usuarios.nome'),
      principal: true,
      render: (m) => (
        <span className="flex min-w-0 flex-col">
          <TextoCortado className="font-medium">
            {/* Val6 (29/10): o nome abre a ficha ligada (Pessoas ou Cadastros) quando quem vê a abre. */}
            <NomeComFicha s={s} ficha={m.ficha} rotulo={t('usuarios.convite.abrirFicha', { nome: m.nome })}>
              {m.nome}
            </NomeComFicha>
            {m.id === s.usuario.id ? ` (${t('usuarios.voce')})` : ''}
          </TextoCortado>
          <span className="truncate text-xs text-tinta-3" title={email(m.email)}>{email(m.email)}</span>
        </span>
      ),
    },
    {
      chave: 'papeis',
      rotulo: t('usuarios.papeis'),
      render: (m) => (
        <span className="flex flex-wrap gap-1">
          {m.papeis.map((p) => (
            <Selo key={p.id} cor={p.codigo === 'dono' ? 'dourado' : 'azul'}>
              {nomeDoPapel(t, p)}
            </Selo>
          ))}
          {m.acesso_personalizado && <Selo cor="roxo">{t('usuarios.acessoPersonalizado')}</Selo>}
          {m.usuario_teste && (
            <span data-selo-usuario-teste="">
              <Selo cor="neutro">{m.usuario_simulacao ? t('usuarios.teste.seloSimulacao') : t('usuarios.teste.selo')}</Selo>
            </span>
          )}
        </span>
      ),
    },
    // Val7 (29/10): os ícones das formas de acesso ATIVAS (e-mail de login, nome de usuário).
    { chave: 'formas', rotulo: t('usuarios.formasAcesso'), render: (m) => <IconesFormas email={m.login_email ?? (!m.sem_email && !!m.email)} usuario={m.login_usuario ?? true} /> },
    { chave: 'ativo', rotulo: t('usuarios.estado'), render: (m) => <Selo cor={m.ativo ? 'verde' : 'neutro'}>{m.ativo ? t('usuarios.ativo') : t('usuarios.inativo')}</Selo> },
    { chave: 'ultimo_acesso', rotulo: t('usuarios.ultimoAcesso'), mono: true, render: (m) => (m.ultimo_acesso ? dataHora(m.ultimo_acesso) : t('usuarios.nunca')) },
    { chave: 'membro_desde', rotulo: t('usuarios.desde'), mono: true, oculta: true, render: (m) => data(m.membro_desde) },
  ];
  return (
    <>
      <Tabela<Membro>
        tela="admin.membros"
        caminho="/admin/membros"
        params={{ situacao, acesso }}
        filtros={{ situacao, acesso }}
        aoAplicarFiltros={(f) => {
          setSituacao(String(f.situacao ?? 'todos'));
          setAcesso(String(f.acesso ?? 'todos'));
        }}
        colunas={colunas}
        aoClicar={(m) => setAberto(m)}
        selecao={
          s.pode('admin.usuarios')
            ? {
                marcados,
                aoAlternar: (m, v) =>
                  setMarcados((x) => {
                    const n = new Set(x);
                    if (v) n.add(m.id);
                    else n.delete(m.id);
                    return n;
                  }),
                aoAlternarTodas: (linhas, v) => setMarcados((x) => new Set(v ? [...x, ...linhas.map((l) => l.id)] : [...x].filter((id) => !linhas.some((l) => l.id === id)))),
                rotulo: (m) => t('usuarios.selecionar', { nome: m.nome }),
              }
            : undefined
        }
        vazio={<Vazio texto={t('usuarios.vazioMembros')} />}
        filtrosExtra={
          <>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('usuarios.filtroSituacao')}
                opcoes={[
                  { valor: 'todos', rotulo: t('usuarios.todos') },
                  { valor: 'ativos', rotulo: t('usuarios.soAtivos') },
                  { valor: 'inativos', rotulo: t('usuarios.soInativos') },
                ]}
                value={situacao}
                onChange={(e) => setSituacao(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('usuarios.filtroAcesso')}
                opcoes={[
                  { valor: 'todos', rotulo: t('usuarios.acessoTodos') },
                  { valor: 'personalizado', rotulo: t('usuarios.acessoPersonalizado') },
                ]}
                value={acesso}
                onChange={(e) => setAcesso(e.target.value)}
              />
            </div>
          </>
        }
      />
      {marcados.size > 0 && (
        <section aria-label={t('usuarios.selecionados', { count: marcados.size })} className="mt-3 flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-borda bg-sup-1 p-3" data-barra-selecao>
          <p className="min-w-0 flex-1 basis-40 text-sm font-medium">{t('usuarios.selecionados', { count: marcados.size })}</p>
          <Botao tamanho="sm" variante="fantasma" onClick={() => setMarcados(new Set())}>
            {t('usuarios.limparSelecao')}
          </Botao>
          <Botao tamanho="sm" data-definir-estilo onClick={() => { setEstiloLote(null); setLoteEstilo(true); }}>
            {t('usuarios.definirEstilo')}
          </Botao>
        </section>
      )}
      <Modal aberto={loteEstilo} aoFechar={() => setLoteEstilo(false)} titulo={t('usuarios.estiloLoteTitulo', { count: marcados.size })}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-tinta-3">{t('usuarios.estiloAjuda')}</p>
          <SeletorEstilo valor={estiloLote} aoEscolher={setEstiloLote} rotulo={t('usuarios.estilo')} padrao={ehEstilo(s.aparencia?.estilo_padrao) ? s.aparencia.estilo_padrao : ESTILO_PADRAO} nomeDoPadrao={false} rotuloPadrao={t('usuarios.estiloLoteSeguir')} compacto />
          <div className="flex justify-end gap-2">
            <Botao variante="fantasma" onClick={() => setLoteEstilo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao carregando={aplicarEstilo.isPending} onClick={() => aplicarEstilo.mutate()}>
              {t('usuarios.estiloLoteAplicar')}
            </Botao>
          </div>
        </div>
      </Modal>
      {aberto && <MembroDrawer key={aberto.id} membro={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

function MembroDrawer({ membro, aoFechar }: { membro: Membro; aoFechar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const papeis = usePapeis();
  const catalogos = useQuery({ queryKey: ['/nucleo/catalogos'], queryFn: () => get<Catalogos>('/nucleo/catalogos') });
  const ehEu = membro.id === s.usuario.id;
  const ehDono = membro.papeis.some((p) => p.codigo === 'dono');
  const [papelIds, setPapelIds] = useState(new Set(membro.papeis.map((p) => p.id)));
  const [empresaIds, setEmpresaIds] = useState(new Set(membro.empresa_ids));
  const [consolidado, setConsolidado] = useState(!!membro.consolidado);
  const [modoSimples, setModoSimples] = useState(!!membro.modo_simples);
  const estiloSalvo: Estilo | null = ehEstilo(membro.estilo_fixado) ? membro.estilo_fixado : null;
  const [estiloFixado, setEstiloFixado] = useState<Estilo | null>(estiloSalvo);
  const [confirmar, setConfirmar] = useState<'desativar' | 'remover' | 'promover' | 'mfa' | null>(null);
  const depois = async (msg: string) => {
    await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
    await qc.invalidateQueries({ queryKey: ['/admin/papeis'] });
    toast.sucesso(msg);
    aoFechar();
  };
  const falha = (e: unknown) => {
    setConfirmar(null);
    toast.erro(mensagemDeErro(e));
  };
  const salvarPapeis = useMutation({ mutationFn: () => api('PUT', `/admin/membros/${membro.id}/papeis`, { papel_ids: [...papelIds] }), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  const salvarEmpresas = useMutation({ mutationFn: () => api('PUT', `/admin/membros/${membro.id}/empresas`, { empresa_ids: [...empresaIds], consolidado }), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  // U15 F5: o administrador liga ou desliga o modo simples da pessoa (preferência de tela, não acesso).
  const salvarEstilo = useMutation({ mutationFn: () => api('PUT', '/admin/membros/estilo', { usuario_ids: [membro.id], estilo: estiloFixado }), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  const salvarModoSimples = useMutation({ mutationFn: () => api('PUT', `/admin/membros/${membro.id}/modo-simples`, { ligado: modoSimples }), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  const desativar = useMutation({ mutationFn: () => post(`/admin/membros/${membro.id}/desativar`), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  const reativar = useMutation({ mutationFn: () => post(`/admin/membros/${membro.id}/reativar`), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  // Lote 19/10 (item 19): o desativado sai da lista e pode ser convidado de novo; nada se apaga.
  const remover = useMutation({ mutationFn: () => post(`/admin/membros/${membro.id}/remover`), onSuccess: () => depois(t('usuarios.removido')), onError: falha });
  const promover = useMutation({ mutationFn: () => post(`/admin/membros/${membro.id}/promover-dono`), onSuccess: () => depois(tn('comum.salvo')), onError: falha });
  // Fase 2 (W4): reset do TOTP de outro membro (perdeu o aparelho e os códigos de backup), auditado no servidor.
  const resetarMfa = useMutation({ mutationFn: () => post('/auth/mfa/resetar', { usuario_id: membro.id }), onSuccess: () => depois(t('usuarios.mfaResetado')), onError: falha });
  // val4 (29/10): conta de demonstração sem login próprio, simulável pelo menu do perfil (só quem tem a permissão marca).
  const marcarTeste = useMutation({
    mutationFn: (teste: boolean) => post('/auth/usuario-teste', { usuario_id: membro.id, teste }),
    onSuccess: (_r, teste) => depois(teste ? t('usuarios.teste.marcado') : t('usuarios.teste.desmarcado')),
    onError: falha,
  });
  const alternar = (conjunto: Set<string>, id: string) => {
    const n = new Set(conjunto);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  };

  return (
    <Drawer aberto aoFechar={aoFechar} titulo={membro.nome}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-tinta-2">{membro.sem_email || !membro.email ? t('usuarios.conta.semEmail') : email(membro.email)}</p>
          {membro.usuario && <p className="numero text-xs break-all text-tinta-3">{anunciarOcultos(t('usuarios.conta.usuarioRotulo', { usuario: nomeDeUsuario(membro.usuario) }))}</p>}
          <p className="flex flex-wrap items-center gap-2 text-xs text-tinta-3">
            <Selo cor={membro.ativo ? 'verde' : 'neutro'}>{membro.ativo ? t('usuarios.ativo') : t('usuarios.inativo')}</Selo>
            <span>
              {t('usuarios.ultimoAcesso')}: {membro.ultimo_acesso ? dataHora(membro.ultimo_acesso) : t('usuarios.nunca')}
            </span>
            <span>
              {t('usuarios.desde')}: {data(membro.membro_desde)}
            </span>
          </p>
        </div>

        <section>
          <h3 className="kicker mb-2">{t('usuarios.papeis')}</h3>
          <Estado consulta={papeis}>
            {(d) => (
              <>
                <ul className="flex flex-col gap-1.5">
                  {d.itens.map((p) => (
                    <li key={p.id}>
                      <label className={`flex items-center gap-2 text-sm ${p.codigo === 'dono' && !ehDono ? 'text-tinta-3' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={papelIds.has(p.id)}
                          disabled={p.codigo === 'dono' && !ehDono}
                          onChange={() => setPapelIds((c) => alternar(c, p.id))}
                        />
                        {nomeDoPapel(t, p)}
                        {!p.de_fabrica && <Selo cor="roxo">{t('usuarios.papel.personalizado')}</Selo>}
                      </label>
                    </li>
                  ))}
                </ul>
                {!ehDono && <p className="mt-2 text-xs text-tinta-3">{t('usuarios.papelDonoAviso')}</p>}
              </>
            )}
          </Estado>
          <div className="mt-3 flex flex-wrap gap-2">
            <Botao variante="primario" disabled={papelIds.size === 0} carregando={salvarPapeis.isPending} onClick={() => salvarPapeis.mutate()}>
              {t('usuarios.salvarPapeis')}
            </Botao>
            {!ehDono && membro.ativo && (
              <Botao icone={<Crown className="size-4" />} onClick={() => setConfirmar('promover')}>
                {t('usuarios.promoverDono')}
              </Botao>
            )}
          </div>
        </section>

        {/* Val1 (28/10): exceção de acesso por pessoa (o Dono tem tudo e não tem exceção; o desativado só vê, rval1-11 ROB-M1). */}
        {!ehDono && <AcessoDoMembro membro={membro} podeEditar={s.pode('admin.usuarios') && membro.ativo} aoSalvar={aoFechar} />}

        <section>
          <h3 className="kicker mb-1">{t('usuarios.empresas')}</h3>
          {membro.pela_ficha ? (
            <p data-membro-pela-ficha="" className="mb-2 text-xs text-tinta-3">
              {t('usuarios.empresasPelaFicha')}
            </p>
          ) : (
            mostraAjudaEmpresas(s, membro.empresa_ids) && <p className="mb-2 text-xs text-tinta-3">{t(chaveAjudaEmpresas(s))}</p>
          )}
          <Estado consulta={catalogos}>
            {(d) => {
              const resumo = resumoEmpresasMembro(
                s,
                membro.empresa_ids,
                d.empresas.map((e) => e.id),
              );
              return d.empresas.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('usuarios.semEmpresas')}</p>
              ) : resumo.veTodas ? (
                <p data-membro-ve-todas="" className="text-sm text-tinta-2">
                  {t('usuarios.empresasVeTodasRestrito')}
                </p>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {d.empresas.map((e) => (
                      <li key={e.id}>
                        <label className={`flex items-center gap-2 text-sm ${membro.pela_ficha ? '' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={empresaIds.has(e.id)} disabled={!!membro.pela_ficha} onChange={() => setEmpresaIds((c) => alternar(c, e.id))} />
                          {e.nome_fantasia || e.razao_social}
                        </label>
                      </li>
                    ))}
                  </ul>
                  {resumo.outras && <p className="mt-1.5 text-xs text-tinta-3">{t('usuarios.empresasOutras')}</p>}
                  {d.empresas.length > 1 && (
                    <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-0.5" checked={consolidado} onChange={(ev) => setConsolidado(ev.target.checked)} />
                      <span className="flex flex-col">
                        {t('usuarios.consolidado')}
                        <span className="text-xs text-tinta-3">{t('usuarios.consolidadoAjuda')}</span>
                      </span>
                    </label>
                  )}
                  <Botao className="mt-3" carregando={salvarEmpresas.isPending} onClick={() => salvarEmpresas.mutate()}>
                    {t('usuarios.salvarEmpresas')}
                  </Botao>
                </>
              );
            }}
          </Estado>
        </section>

        {/* U15 F5: modo simples da pessoa (menos abas e campos avançados). */}
        <section>
          <h3 className="kicker mb-1">{t('usuarios.modoSimples')}</h3>
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={modoSimples} onChange={(ev) => setModoSimples(ev.target.checked)} data-modo-simples-membro />
            <span className="flex flex-col">
              {t('usuarios.modoSimplesLigar')}
              <span className="text-xs text-tinta-3">{t('usuarios.modoSimplesAjuda')}</span>
            </span>
          </label>
          <Botao className="mt-3" disabled={modoSimples === !!membro.modo_simples} carregando={salvarModoSimples.isPending} onClick={() => salvarModoSimples.mutate()}>
            {t('usuarios.modoSimplesSalvar')}
          </Botao>
        </section>

        {/* N5 (28/10): estilo de interface fixado pelo administrador para esta pessoa. */}
        <section data-estilo-membro>
          <h3 className="kicker mb-1">{t('usuarios.estilo')}</h3>
          <p className="mb-2 text-xs text-tinta-3">{t('usuarios.estiloAjuda')}</p>
          <SeletorEstilo valor={estiloFixado} aoEscolher={setEstiloFixado} rotulo={t('usuarios.estilo')} padrao={ehEstilo(s.aparencia?.estilo_padrao) ? s.aparencia.estilo_padrao : ESTILO_PADRAO} nomeDoPadrao={false} rotuloPadrao={t('usuarios.estiloSeguir')} compacto />
          <Botao className="mt-3" disabled={estiloFixado === estiloSalvo} carregando={salvarEstilo.isPending} onClick={() => salvarEstilo.mutate()}>
            {t('usuarios.estiloSalvar')}
          </Botao>
        </section>

        {/* Fase 2 (W22, K8): exceções de aviso deste membro (as mesmas que ele muda em Minha conta). */}
        <section className="border-t border-borda pt-4">
          <ExcecoesMembro usuarioId={membro.id} editavel={s.pode('admin.usuarios')} />
        </section>

        {/* Val7 (29/10, ficha FUN_formas-de-acesso): e-mail de login, nome de usuário e senha (o próprio usa Minha conta). */}
        {!ehEu && s.pode('admin.usuarios') && (
          <div className="border-t border-borda pt-4" data-conta-membro>
            <FormasAcesso usuarioId={membro.id} semSenha={!!membro.usuario_teste} />
          </div>
        )}

        {!ehEu && !ehDono && membro.ativo && s.pode('admin.simular_usuario') && s.pode('admin.usuarios') && (
          <section className="border-t border-borda pt-4" data-usuario-teste-membro="">
            <h3 className="kicker mb-1">{t('usuarios.teste.titulo')}</h3>
            <p className="mb-2 text-xs text-tinta-3">
              {membro.usuario_simulacao ? t('usuarios.teste.textoSimulacao') : membro.sem_email ? t('usuarios.teste.texto') : t('usuarios.teste.soSemEmail')}
            </p>
            {membro.sem_email && !membro.usuario_simulacao && (
              <Botao carregando={marcarTeste.isPending} onClick={() => marcarTeste.mutate(!membro.usuario_teste)}>
                {membro.usuario_teste ? t('usuarios.teste.desmarcar') : t('usuarios.teste.marcar')}
              </Botao>
            )}
          </section>
        )}

        {!ehEu && (
          <section className="border-t border-borda pt-4" data-reset-mfa>
            <h3 className="kicker mb-1">{t('usuarios.mfaTitulo')}</h3>
            <p className="mb-2 text-xs text-tinta-3">{t('usuarios.mfaResetarTexto')}</p>
            <Botao icone={<ShieldOff className="size-4" />} onClick={() => setConfirmar('mfa')}>
              {t('usuarios.mfaResetar')}
            </Botao>
          </section>
        )}

        <section className="border-t border-borda pt-4">
          {membro.ativo ? (
            <>
              <Botao variante="perigo" icone={<UserX className="size-4" />} disabled={ehEu} onClick={() => setConfirmar('desativar')}>
                {t('usuarios.desativar')}
              </Botao>
              {ehEu && <p className="mt-2 text-xs text-tinta-3">{t('usuarios.autoDesativacao')}</p>}
            </>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Botao icone={<UserCheck className="size-4" />} carregando={reativar.isPending} onClick={() => reativar.mutate()}>
                {t('usuarios.reativar')}
              </Botao>
              {!ehEu && (
                <Botao variante="perigo" icone={<UserMinus className="size-4" />} onClick={() => setConfirmar('remover')}>
                  {t('usuarios.remover')}
                </Botao>
              )}
            </div>
          )}
        </section>
      </div>

      <Confirmar
        aberto={confirmar === 'desativar'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => desativar.mutate()}
        carregando={desativar.isPending}
        perigoso
        titulo={t('usuarios.desativarTitulo', { nome: membro.nome })}
        texto={t('usuarios.desativarTexto')}
      />
      <Confirmar
        aberto={confirmar === 'remover'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => remover.mutate()}
        carregando={remover.isPending}
        perigoso
        titulo={t('usuarios.removerTitulo', { nome: nomePessoa(membro.nome) })}
        texto={t('usuarios.removerTexto')}
      />
      <Confirmar
        aberto={confirmar === 'mfa'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => resetarMfa.mutate()}
        carregando={resetarMfa.isPending}
        perigoso
        titulo={t('usuarios.mfaResetar')}
        texto={t('usuarios.mfaResetarTexto')}
      />
      <Confirmar
        aberto={confirmar === 'promover'}
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => promover.mutate()}
        carregando={promover.isPending}
        titulo={t('usuarios.promoverTitulo', { nome: membro.nome })}
        texto={t('usuarios.promoverTexto')}
      />
    </Drawer>
  );
}
